/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, MouseEventHandler, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import IonTooltip from '../../components/tooltip/Tooltip';
import {MaterialBackIcon} from '../../icons/MaterialBackIcon';
import {MaterialForwardIcon} from '../../icons/MaterialForwardIcon';
import {dlsColor} from '../../theme/theme';
import {useNavigationStyle} from '../tooltip/Tooltip';
import {toggleCollapseStyle} from './useExpandCollapse';

// defining the props definition for the ExpandCollapseToggle...
export interface ExpandCollapseToggleProps {
  onClick: Function;
  enabled: boolean;
  collapsed: boolean;
  toolTipArrow?: boolean;
  toolTipPosition?: string;
};

// defining the styles definition for the ExpandCollapseToggle...
const useStyles = makeStyles((theme) => ({
  root : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : toggleCollapseStyle('center', 'start'),
    width          : '100%',
    margin         : '8px 0'
  },
  text : {
    margin        : '-3px 0 0 -6px',
    textTransform : 'uppercase',
    fontSize      : '11px',
    color         : dlsColor('white')
  },
  icon : {
    '&:hover' : {
      transform          : 'translate3d(0, 0, 0)',
      backfaceVisibility : 'hidden',
      perspective        : '1000px'
    }
  }
}));

/**
 * Defining the ExpandCollapseToggle component. This component is render the expand
 * and collapse toggle functionality buttons in the navigation bar.
 *
 * ```typescript
 *
 *  import {ExpandCollapseToggle} from './ExpandCollapseToggle';
 *
 *  <ExpandCollapseToggle enabled
 *    onClick={onClickHandler}
 *    collapsed={collapsed} />
 *
 * ```
 */
export const ExpandCollapseToggle = ({
  onClick,
  enabled = true,
  collapsed = false,
  toolTipArrow = false,
  toolTipPosition = 'bottom'
}: ExpandCollapseToggleProps) => {
  const classes         = useStyles({collapsed});
  const toolTipClassess = useNavigationStyle();

  const {
    collapse: collapseLabel,
    expand: expandLabel
  } = useMemo(() => (translate('nav:menu', {returnObjects: true})), []);

  const toggleButton = useMemo(() => (
    <div className={classes.root} onClick={onClick as MouseEventHandler}>
      <div className={classes.icon}>
        {collapsed ? (
          <MaterialForwardIcon color={dlsColor('aaBlue')} />
        ) : (
          <MaterialBackIcon color={dlsColor('aaBlue')} />
        )}
      </div>
      {!collapsed && (
        <span className={classes.text}>{collapseLabel}</span>
      )}
    </div>
  ), [collapsed, classes, onClick]);

  if (enabled) {
    return (
      <>
        {(collapsed ? (
          <IonTooltip title={<span style={{marginLeft: '8px'}}>{expandLabel}</span>}
            placement={toolTipPosition as any}
            externalClasses={toolTipClassess}
            arrow={toolTipArrow}>
            {toggleButton}
          </IonTooltip>
        ) : (
          toggleButton
        ))}
      </>
    );
  }
};

export default memo(ExpandCollapseToggle);
